import { Button, Dropdown, RadioButton, RadioButtonGroup, TextInput } from '@carbon/react';
import { Add } from '@carbon/icons-react';
import {
  asProblem,
  FormModal,
  formValues,
  hasPermission,
  missingRequired,
  problemMessage,
  ResourceForm,
  useAdmin,
  useReferenceOptions,
  useResourceApi,
  type RecordValues,
} from '@pos/admin-ui';
import type { ApiProblem } from '@pos/api-client';
import { useTranslation } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import './options.scss';

type Kind = 'text' | 'item';

interface PostClient {
  POST(path: string, init: { body: unknown }): Promise<{ data?: unknown; error?: unknown; response: Response }>;
}

/**
 * *New option item* (ADR 0029, FR-CAT-04, FR-CAT-06), two ways:
 * - **Text only**: a free option such as "Ít hành", typed by its name. HO gives it the default tax category and unit and a
 *   code from its name (`POST /api/v1/catalog/text-options`).
 * - **Priced or with details**: the item form with the type *Only as an option* chosen and the default (else the first) tax
 *   category and unit filled in.
 * On an option group's *Options* tab (`group`) the new item is also added to that group; on the items list it only creates it.
 */
export function OptionItemButton({ group, refresh }: { group?: RecordValues; refresh(): void }) {
  const { t } = useTranslation();
  const { session, resources, client } = useAdmin();
  const itemDef = resources['catalog.item'];
  const optionDef = resources['catalog.modifier'];
  const taxDef = resources['catalog.tax_category'];
  const unitDef = resources['catalog.uom'];
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<Kind>('text');
  const [text, setText] = useState({ name: '', nameEn: '', brandId: '' });
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [submitted, setSubmitted] = useState(false);
  const itemApi = useResourceApi(itemDef as NonNullable<typeof itemDef>);
  const optionApi = useResourceApi(optionDef as NonNullable<typeof optionDef>, group?.id);
  const taxApi = useResourceApi(taxDef as NonNullable<typeof taxDef>);
  const unitApi = useResourceApi(unitDef as NonNullable<typeof unitDef>);
  const queryClient = useQueryClient();
  const taxes = useReferenceOptions('catalog.tax_category');
  const units = useReferenceOptions('catalog.uom');
  const brands = useReferenceOptions('org.brand');
  // The tenant's defaults (FR-CAT-06): a text option takes them, and the item form starts with them.
  const defaults = useQuery({
    queryKey: ['reference', 'option-defaults'],
    enabled: open && Boolean(taxDef && unitDef),
    queryFn: async () => ({
      taxCategoryId: (await taxApi.list({ isDefault: 'true', pageSize: 1 })).items[0]?.id,
      baseUomId: (await unitApi.list({ isDefault: 'true', pageSize: 1 })).items[0]?.id,
    }),
  });

  const groupBrand = typeof group?.['brandId'] === 'string' ? group['brandId'] : undefined;
  const brandId = groupBrand ?? (text.brandId || brands.data?.[0]?.value || '');
  // Required references start as the defaults (else the first tax category and unit) and the group's brand, until the user picks.
  const current = {
    ...values,
    taxCategoryId: values['taxCategoryId'] ?? defaults.data?.taxCategoryId ?? taxes.data?.[0]?.value ?? null,
    baseUomId: values['baseUomId'] ?? defaults.data?.baseUomId ?? units.data?.[0]?.value ?? null,
    brandId: values['brandId'] ?? brands.data?.[0]?.value ?? null,
  };

  const done = () => {
    close();
    refresh();
    void queryClient.invalidateQueries({ queryKey: ['reference'] });
  };

  const saveItem = useMutation({
    mutationFn: async (input: Record<string, unknown>) => {
      const item = await itemApi.create(input);
      if (group) await optionApi.create({ itemId: item.id, isDefault: false, maxQuantity: null, sortOrder: 0 });
    },
    onSuccess: done,
  });

  const saveText = useMutation({
    mutationFn: async () => {
      const { error, response } = await (client as PostClient).POST('/api/v1/catalog/text-options', {
        body: {
          brandId,
          name: text.name.trim(),
          nameI18n: text.nameEn.trim() ? { en: text.nameEn.trim() } : null,
          modifierGroupId: group?.id ?? null,
        },
      });
      if (!response.ok) throw { ...((error ?? {}) as ApiProblem), status: response.status } satisfies ApiProblem;
    },
    onSuccess: done,
  });

  if (!itemDef || !optionDef || !hasPermission(session, itemDef.writePermission ?? undefined)) return null;

  function close() {
    saveItem.reset();
    saveText.reset();
    setSubmitted(false);
    setOpen(false);
    setKind('text');
    setText({ name: '', nameEn: '', brandId: '' });
    setValues({});
  }

  const start = () => {
    setValues({ ...formValues(itemDef), itemType: 'modifier_only', brandId: groupBrand ?? null });
    setOpen(true);
  };

  const submit = () => {
    setSubmitted(true);
    if (kind === 'text') {
      if (text.name.trim() && brandId) saveText.mutate();
      return;
    }
    if (missingRequired(itemDef, current, false).length === 0) saveItem.mutate(current);
  };

  const error = kind === 'text' ? saveText.error : saveItem.error;
  const problem = error ? asProblem(error) : undefined;
  const nameMissing = submitted && !text.name.trim();
  const brandChoices = (brands.data ?? []).map((b) => ({ id: b.value, label: b.label }));

  return (
    <>
      <Button kind={group ? 'tertiary' : 'ghost'} renderIcon={Add} onClick={start}>
        {t('optionItem.button')}
      </Button>
      {open &&
        createPortal(
          <FormModal
            title={t('optionItem.title')}
            label={t(itemDef.title)}
            pending={saveItem.isPending || saveText.isPending}
            error={problem && !problem.errors && !problem.field ? problemMessage(t, problem) : null}
            onSubmit={submit}
            onClose={close}
          >
            <RadioButtonGroup
              legendText={t('optionItem.kind')}
              name="option-kind"
              valueSelected={kind}
              onChange={(value) => {
                setKind(value as Kind);
                setSubmitted(false);
              }}
            >
              <RadioButton id="option-kind-text" value="text" labelText={t('optionItem.kindText')} />
              <RadioButton id="option-kind-item" value="item" labelText={t('optionItem.kindItem')} />
            </RadioButtonGroup>

            {kind === 'text' ? (
              <div className="pos-option-item__text">
                <p className="pos-option-item__hint">{t(group ? 'optionItem.textHintGroup' : 'optionItem.textHint')}</p>
                <TextInput
                  id="text-option-name"
                  labelText={t('optionItem.name')}
                  placeholder={t('optionItem.namePlaceholder')}
                  value={text.name}
                  maxLength={200}
                  invalid={nameMissing || problem?.field === 'name'}
                  invalidText={
                    nameMissing ? t('optionItem.nameRequired') : problem ? problemMessage(t, problem) : undefined
                  }
                  onChange={(event) => setText((s) => ({ ...s, name: event.target.value }))}
                />
                <TextInput
                  id="text-option-name-en"
                  labelText={t('optionItem.nameEn')}
                  value={text.nameEn}
                  maxLength={200}
                  onChange={(event) => setText((s) => ({ ...s, nameEn: event.target.value }))}
                />
                {!groupBrand && (
                  <Dropdown
                    id="text-option-brand"
                    titleText={t('md.fields.brand_id')}
                    label=""
                    items={brandChoices}
                    itemToString={(item) => item?.label ?? ''}
                    selectedItem={brandChoices.find((b) => b.id === brandId) ?? null}
                    onChange={({ selectedItem }) => setText((s) => ({ ...s, brandId: selectedItem?.id ?? '' }))}
                  />
                )}
              </div>
            ) : (
              <>
                <p className="pos-option-item__hint">{t(group ? 'optionItem.hintGroup' : 'optionItem.hint')}</p>
                <ResourceForm
                  def={itemDef}
                  values={current}
                  error={saveItem.error}
                  onChange={setValues}
                  onSubmit={submit}
                  submitted={submitted}
                  bare
                />
              </>
            )}
          </FormModal>,
          document.body,
        )}
    </>
  );
}
